import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { firstValueFrom } from 'rxjs';
import { LoginPlatform, getLoginPlatformBadge } from '../../shared/utils/login-platform.utils';
import { getMembershipPlanBadge } from '../../shared/utils/membership-plan.utils';

interface UserProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  emailVerified: boolean;
  is2faEnabled: boolean;
  twoFactorMethod: '2fa-sms' | '2fa-totp' | null;
  firstLoginLocation: LoginPlatform | null;
  lastLoginLocation: LoginPlatform | null;
  isLocked: boolean;
  membershipPlan: 'core' | 'pro';
  createdAt: string;
  updatedAt: string;
}

interface PaginatedResponse {
  users: UserProfile[];
  total: number;
  page: number;
  pageSize: number;
}

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss'
})
export class UsersComponent implements OnInit {
  users: UserProfile[] = [];
  loading = true;
  errorMessage = '';

  // Pagination
  currentPage = 1;
  pageSize = 20;
  totalUsers = 0;
  totalPages = 0;

  // Filters
  searchQuery = '';
  filterEmailVerified: 'all' | 'verified' | 'unverified' = 'all';
  filter2FA: 'all' | 'enabled' | 'disabled' = 'all';
  filterLocked: 'all' | 'locked' | 'unlocked' = 'all';
  filterMembershipPlan: 'all' | 'core' | 'pro' = 'all';
  sortBy: 'createdAt' | 'email' | 'firstName' = 'createdAt';
  sortOrder: 'asc' | 'desc' = 'desc';

  constructor(
    private http: HttpClient,
    private router: Router
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadUsers();
  }

  async loadUsers(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      // Build query parameters
      const params: any = {
        page: this.currentPage,
        pageSize: this.pageSize,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder
      };

      if (this.searchQuery) {
        params.search = this.searchQuery;
      }

      if (this.filterEmailVerified !== 'all') {
        params.emailVerified = this.filterEmailVerified === 'verified';
      }

      if (this.filter2FA !== 'all') {
        params.is2faEnabled = this.filter2FA === 'enabled';
      }

      if (this.filterLocked !== 'all') {
        params.isLocked = this.filterLocked === 'locked';
      }

      if (this.filterMembershipPlan !== 'all') {
        params.membershipPlan = this.filterMembershipPlan;
      }

      // Call the backend API
      const response = await firstValueFrom(
        this.http.get<PaginatedResponse>(`${environment.apiUrl}/admin/users`, { params })
      );

      this.users = response.users;
      this.totalUsers = response.total;
      this.totalPages = Math.ceil(this.totalUsers / this.pageSize);

    } catch (error: any) {
      console.error('Load users error:', error);
      this.errorMessage = 'Failed to load users';
    } finally {
      this.loading = false;
    }
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadUsers();
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadUsers();
  }

  showClearFilters(): boolean {
    return !!(this.searchQuery || this.filterEmailVerified !== 'all' || this.filter2FA !== 'all' || this.filterLocked !== 'all' || this.filterMembershipPlan !== 'all' || this.sortBy !== 'createdAt' || this.sortOrder !== 'desc');
  }

  resetFilters(): void {
    this.searchQuery = '';
    this.filterEmailVerified = 'all';
    this.filter2FA = 'all';
    this.filterLocked = 'all';
    this.filterMembershipPlan = 'all';
    this.sortBy = 'createdAt';
    this.sortOrder = 'desc';
    this.onFilterChange();
  }

  onSortChange(): void {
    this.loadUsers();
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadUsers();
    }
  }

  viewUser(userId: string): void {
    this.router.navigate(['/users', userId]);
  }

  createUser(): void {
    this.router.navigate(['/users/create']);
  }

  createUserOrganization(): void {
    this.router.navigate(['/users/create-organization']);
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  readonly getLoginPlatformBadge = getLoginPlatformBadge;
  readonly getMembershipPlanBadge = getMembershipPlanBadge;

  get paginationRange(): number[] {
    const range: number[] = [];
    const maxButtons = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2));
    let end = Math.min(this.totalPages, start + maxButtons - 1);

    if (end - start < maxButtons - 1) {
      start = Math.max(1, end - maxButtons + 1);
    }

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    return range;
  }

  // Expose Math for template
  readonly Math = Math;
}
